/**
 * ثبت و مدیریت تولید — لیستِ مدیریتیِ ردیف‌های محصول (محصول | رنگ | سایز) با
 * مقایسهٔ سه مقدار اصلی (برش / کنترل اول / سالم+زده‌دار)، ویرایش دسته‌جمعی، خروجی
 * اکسل و جمع‌های زیر لیست. کنترل اول و کنترل نهایی و عملیات، داخل جزئیات هر کد تولید.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button, Card, Col, Input, Row, Select, Space, Statistic, Tag, Tooltip, Typography, message,
} from "antd";
import { DownloadOutlined, FolderOpenOutlined, ReloadOutlined } from "@ant-design/icons";
import EditableTable from "../../components/EditableTable";
import DismissibleHelp from "../../components/DismissibleHelp";
import { useMonthScope } from "../../components/MonthScope";
import BijackDrawer from "./BijackDrawer";
import {
  getProductionOptions, getProductionRows, productionRowsBulkUpdate,
  getProductionMonth, closeProductionMonth, reopenProductionMonth,
} from "../../api/client";
import { JalaliDateInput, toJalali, fmtNum } from "../../utils/dateUtils";
import { byNum, byStr, byDate } from "../../utils/sorters";
import { exportCsv } from "../../utils/tableExport";
import useColorOptions from "../../hooks/useColorOptions";
import { PRODUCTION_STAGES, STAGE_MAP } from "../../utils/productionStatus";
import {
  usePayrollMonthOptions, useClosedPayrollMonths, joinPayrollMonth, splitPayrollMonth, payrollMonthLabel,
} from "../../utils/payrollMonth";

const { Title, Text } = Typography;

const STAGE_OPTS = [
  { value: "cut", label: "برش" },
  { value: "sewing", label: "دوخت" },
  { value: "qc1", label: "کنترل اول" },
  { value: "qc2", label: "کنترل نهایی" },
];
// وضعیتِ دوخت (بر پایهٔ حقوقِ دوزنده‌ها) — ترتیب: آماده دوخت → نیمه‌کاره → تمام‌شده


const STATUS_LABELS = {
  open: "باز", in_production: "در حال تولید", qc1_done: "کنترل ۱ ✓",
  qc2_done: "کنترل ۲ ✓", packaging: "بسته‌بندی", closed: "بسته شده",
};

const diffTag = (value, pct) => {
  if (value == null) return <Text type="secondary">—</Text>;
  if (value === 0) return <Tag color="green">۰</Tag>;
  return (
    <Tag color={value < 0 ? "red" : "orange"}>
      {value > 0 ? "+" : "−"}{fmtNum(Math.abs(value))}{pct != null && ` (${fmtNum(Math.abs(pct))}٪)`}
    </Tag>
  );
};

/* ستون‌هایی که پیش‌فرض بسته‌اند — دیدِ روزانه را خلوت می‌کنند، نه اینکه
   اطلاعات را از بین ببرند. سه‌تای اول تکرارِ محضِ ستونِ «محصول | رنگ | سایز»اند. */
const PROD_DEFAULT_HIDDEN = [
  "product_type", "color", "size_label",   // تکرارِ ستونِ display
  "bijack_no",                             // در جست‌وجوی بالای صفحه هست
  "notes",                                 // پهن‌ترین ستونِ متنی، اغلب خالی
  "sewing_qty", "waste_qty",               // اعدادِ تطبیقی، نه ورودیِ روزانه
  "qc1_diff",                              // مشتق؛ هر دو عملوندش دیده می‌شوند
  "cutter_name",                           // فقط‌خواندنی و کارِ صفحهٔ «ثبت برش»
];

export default function ProductionManagePage() {
  const monthOpts = usePayrollMonthOptions();      // ماه‌های حقوقِ بسته غیرفعال (v2.152.12)
  const closedMonths = useClosedPayrollMonths();   // v2.158.0: کارِ ماهِ بسته قفل است
  const { code: codeParam } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [opts, setOpts] = useState({ product: [], color: [], size: [] });
  // رنگ از کاتالوگِ مشترک — با ثبتِ سراسریِ رنگِ تازه در همان لحظه
  const { colorOptions, registerColor } = useColorOptions();
  const [loading, setLoading] = useState(false);
  const [openCode, setOpenCode] = useState(codeParam || null);
  // هر محصولِ یک کد تولید، صفحهٔ تولیدِ جدای خودش را دارد (deep-link با ?order=)
  const [openOrderId, setOpenOrderId] = useState(
    Number(searchParams.get("order")) || null);

  const [q, setQ] = useState("");
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [stage, setStage] = useState(null);
  const [status, setStatus] = useState(null);
  const [from, setFrom] = useState(null);
  const [progress, setProgress] = useState(null);
  // v2.152.6: «بدونِ ماهِ محاسبه» — کارهایی که هنوز ماهِ حقوق نخورده‌اند
  const [noMonth, setNoMonth] = useState(false);
  const [noMonthCount, setNoMonthCount] = useState(0);
  // دورهٔ ماهانه — پیش‌فرض ماهِ جاری، per user یادش می‌ماند
  const monthScope = useMonthScope("production-manage");
  // «تولیدِ این ماه تمام شد» — محاسبهٔ حقوق به همین نگاه می‌کند
  const [monthClose, setMonthClose] = useState(null);
  const loadMonthClose = useCallback(() => {
    if (!monthScope.jy) { setMonthClose(null); return; }
    getProductionMonth(monthScope.jy, monthScope.jm)
      .then((r) => setMonthClose(r.data)).catch(() => {});
  }, [monthScope.jy, monthScope.jm]);
  useEffect(() => { loadMonthClose(); }, [loadMonthClose]);

  const toggleMonthClose = async () => {
    try {
      if (monthClose?.status === "closed") {
        await reopenProductionMonth(monthScope.jy, monthScope.jm);
        message.success("تولیدِ ماه بازگشایی شد");
      } else {
        await closeProductionMonth({ year: monthScope.jy, month: monthScope.jm });
        message.success("تولیدِ ماه بسته شد");
      }
      loadMonthClose();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getProductionRows({
        q: q.trim() || undefined, product_type: product || undefined,
        color: color || undefined, size_label: size || undefined,
        stage: stage || undefined, status: status || undefined,
        progress: progress || undefined, date_from: from || undefined,
        no_payroll_month: noMonth || undefined,
        // سمتِ سرور: «ماهِ محاسبهٔ حقوق» (صریح، وگرنه تاریخِ برش/کار)
        ...(monthScope.jy && !from ? { jy: monthScope.jy, jm: monthScope.jm } : {}),
      });
      // rowKey عددی لازم است (ذخیرهٔ دسته‌جمعی) — ردیف‌ها با rid شماره می‌گیرند
      setRows((r.data.rows || []).map((row, i) => ({
        ...row, rid: i + 1,
        payroll_key: joinPayrollMonth(row.payroll_year, row.payroll_month),
      })));
      setTotals(r.data.totals || {});
      if (!noMonth) setNoMonthCount((r.data.rows || []).filter((x) => !x.payroll_year).length);
      setStatusOptions(r.data.status_options || []);
    } catch {
      message.error("خطا در بارگذاری ردیف‌های تولید");
    }
    setLoading(false);
  }, [q, product, color, size, stage, status, progress, from, noMonth, monthScope.value]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getProductionOptions({ active_only: true }).then((r) => {
      const g = { product: [], color: [], size: [] };
      (r.data || []).forEach((o) => g[o.kind]?.push({ value: o.value, label: o.value }));
      setOpts(g);
    }).catch(() => {});
  }, []);

  const openDrawer = (code, orderId) => {
    setOpenCode(code);
    setOpenOrderId(orderId ?? null);
    nav(`/production/${encodeURIComponent(code)}${orderId ? `?order=${orderId}` : ""}`);
  };
  const closeDrawer = () => { setOpenCode(null); setOpenOrderId(null); nav("/production"); };

  const columns = useMemo(() => [
    { title: "محصول | رنگ | سایز", dataIndex: "display", sorter: byStr("display"), width: 240, fixed: "right",
      render: (v, r) => <a onClick={() => openDrawer(r.production_code, r.order_id)}>{v}</a> },
    { title: "کد تولید", dataIndex: "production_code", sorter: byStr("production_code"), width: 150, editType: "text",
      render: (v) => <Tag color="blue">{v || "—"}</Tag> },
    { title: "شماره بیجک", dataIndex: "bijack_no", sorter: byStr("bijack_no"), width: 120, editType: "text" },
    { title: "تاریخ", dataIndex: "entry_date", sorter: byDate("entry_date"), width: 115, editType: "date",
      render: (v) => toJalali(v) },
    // همان «ماه محاسبه»ی صفحهٔ برش: کارِ خرداد ممکن است در حقوقِ تیر بنشیند،
    // پس باید همین‌جا هم دیده و ویرایش شود نه فقط در ثبتِ برش.
    // مرتب‌سازی روی ماهِ **مؤثر** (صریح ← ماهِ تاریخِ برش) تا ردیفِ بی‌ماه هم سرِ
    // جای درستش بنشیند — «ماه» در این صفحه یعنی ماهِ محاسبهٔ حقوق.
    { title: "ماه محاسبه", dataIndex: "payroll_key", width: 150,
      sorter: byStr("payroll_eff_key"), editType: "select", editAllowClear: true,
      options: monthOpts,
      render: (_, r) => {
        const lbl = payrollMonthLabel(r.payroll_year, r.payroll_month);
        if (!lbl) {
          /* v2.152.6 (کاربر ۱۴۰۵/۰۸/۰۲): «آن‌هایی که ماهِ محاسبه نخورده‌اند باید خالی بمانند، نه "شهریور ۱۴۰۵ طبقِ برش" —
             قاطی می‌شود». ماهی که موتور فعلاً برایش در نظر می‌گیرد فقط در راهنمای روی خط تیره است. */
          const keyLbl = (k) => { const [y, m] = String(k || "").split("-").map(Number); return payrollMonthLabel(y, m); };
          const cutLbl = keyLbl(r.payroll_cut_key);
          const sewLbls = (r.payroll_sew_keys || []).map(keyLbl).filter(Boolean);
          const hint = "ماهِ محاسبه تعیین نشده"
            + (cutLbl ? ` — فعلاً برشش در ${cutLbl}` : "")
            + (sewLbls.length ? ` و دوختش در ${sewLbls.join(" و ")} شمرده می‌شود` : "؛ هنوز دوختی ندارد");
          return <Tooltip title={hint}><Text type="secondary">—</Text></Tooltip>;
        }
        /* v2.158.0 (کاربر ۱۴۰۵/۰۸/۰۷): «اگر محاسبهٔ حقوق بسته شد، ماهِ محاسبه را نشود عوض کرد» — سرور رد می‌کند؛ اینجا قفل دیده می‌شود */
        // v2.163.1: قفل فقط برای ردیفی که دوخت دارد؛ پارتِ خالی با ماهِ بستهٔ اشتباه قابلِ اصلاح است
        if (closedMonths.has(`${r.payroll_year}-${r.payroll_month}`) && (r.payroll_sew_keys || []).length) {
          return <Tooltip title="حقوقِ این ماه بسته شده — ماهِ محاسبه و دوختش قفل است (کنترلِ نهایی آزاد است)">
            <span>🔒 {lbl}</span></Tooltip>;
        }
        // «فقط همین سایز» یعنی این سایز از بقیهٔ پارت جدا شده — بدونِ این نشانه
        // معلوم نیست چرا دو ردیفِ یک پارت دو ماهِ متفاوت دارند.
        return r.payroll_scope === "size"
          ? <Tooltip title="فقط برای همین سایز تنظیم شده"><span>{lbl} ✳</span></Tooltip>
          : lbl;
      } },
    { title: "محصول", dataIndex: "product_type", sorter: byStr("product_type"), width: 170, editType: "select",
      options: opts.product },
    { title: "رنگ", dataIndex: "color", sorter: byStr("color"), width: 110,
      // رنگِ تازه هم پذیرفته می‌شود: تایپ کن و Enter بزن — همان لحظه در کاتالوگِ
      // مشترک ثبت می‌شود تا در دراپ‌داون‌های بعدی (تولید و فاکتور) هم بیاید
      editType: "select", editAllowNew: true, options: colorOptions,
      editOnCreate: registerColor },
    { title: "سایز", dataIndex: "size_label", sorter: byStr("size_label"), width: 120, editType: "select", options: opts.size,
      render: (v, r) => r.orphan_size
        ? <Tooltip title="این سایز در بیجکِ برش نیست ولی دوخت/کنترل دارد؛ در صفحهٔ پارت با «افزودن به بیجک» درستش کنید">
            <span>{v} <Tag color="red" style={{ marginInlineStart: 4 }}>بدونِ برش</Tag></span>
          </Tooltip>
        : v },
    { title: "برشکار", dataIndex: "cutter_name", sorter: byStr("cutter_name"), width: 150, render: (v) => v || "—" },
    { title: "بیجک برش", dataIndex: "cut_qty", sorter: byNum("cut_qty"), width: 105, align: "center",
      render: (v) => <b>{fmtNum(v)}</b> },
    // دانهٔ دوخته‌شده (بیشترین جمع در یک عملیات) — نه جمعِ همهٔ عملیات (v2.117.0)
    { title: "دوخت", dataIndex: "sewing_qty", sorter: byNum("sewing_qty"), width: 90, align: "center",
      render: (v, r) => (r.sewing_ops_total && r.sewing_ops_total !== v
        ? <Tooltip title={`دانه‌های دوخته‌شده. جمعِ همهٔ عملیات: ${fmtNum(r.sewing_ops_total)}`}><span>{fmtNum(v)}</span></Tooltip>
        : fmtNum(v)) },
    // v2.119.0 (کاربر): «عملیاتِ دوختِ ثبت‌شده — بفهمیم اصلاً ثبت شده یا نه و چقدر»
    { title: "عملیات دوخت ثبت‌شده", dataIndex: "sewing_ops_count", sorter: byNum("sewing_ops_count"), width: 150,
      align: "center",
      render: (v, r) => {
        if (!v) return <Tag color="default">ثبت نشده</Tag>;
        const list = r.sewing_ops || [];
        return (
          <Tooltip title={<div>{list.map((op, i) => (
            <div key={i}>{op.name}: <b>{fmtNum(op.qty)}</b></div>))}
            <div style={{ marginTop: 4, opacity: 0.8 }}>{fmtNum(r.sewing_entries)} ثبت</div></div>}>
            <Tag color={v >= 2 ? "blue" : "gold"} style={{ cursor: "help" }}>{fmtNum(v)} عملیات</Tag>
          </Tooltip>
        );
      } },
    { title: "کنترل اول", dataIndex: "qc1_qty", sorter: byNum("qc1_qty"), width: 100, align: "center", render: fmtNum },
    { title: "اختلاف کنترل اول", key: "qc1_diff", dataIndex: "qc1_diff", sorter: byNum("qc1_diff"), width: 145, align: "center",
      render: (v, r) => diffTag(v, r.qc1_diff_pct) },
    { title: "سالم", dataIndex: "good_qty", sorter: byNum("good_qty"), width: 85, align: "center", render: fmtNum },
    { title: "زده‌دار", dataIndex: "defect_qty", sorter: byNum("defect_qty"), width: 90, align: "center", render: fmtNum },
    { title: "کنترل نهایی", dataIndex: "final_qty", sorter: byNum("final_qty"), width: 115, align: "center",
      render: (v) => <b>{fmtNum(v)}</b> },
    { title: "اختلاف کنترل نهایی", key: "final_diff", dataIndex: "final_diff", sorter: byNum("final_diff"), width: 155,
      align: "center", render: (v, r) => diffTag(v, r.final_diff_pct) },
    { title: "ضایعات", dataIndex: "waste_qty", sorter: byNum("waste_qty"), width: 90, align: "center", render: fmtNum },
    // یک وضعیتِ واحد به‌جای سه ستونِ تکراریِ «وضعیت دوخت» + «مرحله» + «وضعیت».
    // وضعیتِ داخلیِ بک‌اند (status) حذف نشده — فقط در tooltip دیده می‌شود.
    { title: "وضعیت", dataIndex: "progress", sorter: byStr("progress"), width: 150,
      gxFilter: "enum",
      gxFilterOptions: PRODUCTION_STAGES.map((s) => ({ text: s.label, value: s.value })),
      render: (v, r) => (
        <Tooltip title={STAGE_MAP[v]?.hint
          + (r.status ? ` · وضعیتِ داخلی: ${STATUS_LABELS[r.status] || r.status}` : "")}>
          <Tag color={STAGE_MAP[v]?.color}>{STAGE_MAP[v]?.label || v}</Tag>
        </Tooltip>
      ) },
    /* «تاریخِ کار» و «تاریخِ ثبت» دو چیزِ متفاوت‌اند: پارتی که ۱۰ مرداد بریده
       شده ممکن است ۲۰ مرداد وارد سیستم شود. ترتیبِ پیش‌فرضِ فهرست بر پایهٔ
       همین ستون است، پس باید دیده شود. */
    { title: "تاریخ ثبت", dataIndex: "created_at", key: "created_at", width: 125,
      sorter: byStr("created_at"),
      render: (v) => (v ? (
        <Tooltip title={new Date(v).toLocaleString("fa-IR")}>
          {/* 🐞 قبلاً `slice(0,10)` آفستِ «+00:00» را دور می‌ریخت و بیجکی که
              ۰۰:۳۰ به وقتِ تهران ثبت شده بود، یک روز عقب‌تر نشان داده می‌شد —
              آن‌هم درست کنارِ tooltipی که تاریخِ درست را می‌گفت. */}
          <span>{toJalali(v)}</span>
        </Tooltip>
      ) : "—") },
    { title: "یادداشت", dataIndex: "notes", sorter: byStr("notes"), width: 180, editType: "textarea",
      render: (v) => v || "—" },
    { title: "", width: 55, fixed: "left", gxExport: false,
      render: (_, r) => (
        <Tooltip title="صفحهٔ تولیدِ همین محصول">
          <Button size="small" icon={<FolderOpenOutlined />}
            onClick={() => openDrawer(r.production_code, r.order_id)} />
        </Tooltip>
      ) },
  ], [opts, statusOptions, colorOptions, registerColor, monthOpts, closedMonths]);

  const gxSummary = [
    "cut_qty", "sewing_qty", "qc1_qty", "good_qty", "defect_qty", "final_qty", "waste_qty",
  ].map((dataIndex) => ({ dataIndex, type: "sum" }));

  const saveMany = async (items) => {
    const byRid = new Map(rows.map((r) => [r.rid, r]));
    const payload = items.map(({ id, payroll_key, ...fields }) => {
      const row = byRid.get(Number(id)) || {};
      const item = { order_id: row.order_id, ...fields };
      if (fields.size_label != null) item.size_id = row.size_id;
      // `payroll_key` ستونِ نمایشی است؛ سرور سال و ماه را جدا می‌خواهد.
      // مقدارِ خالی عمداً `null` فرستاده می‌شود = «طبقِ تاریخِ کار».
      // دامنه هم **همین سایز** است: هر ردیفِ این لیست یک سایز است و ماهِ
      // محاسبه per سایز تنظیم می‌شود (XL تیر، ۲XL مرداد).
      if (payroll_key !== undefined) {
        Object.assign(item, splitPayrollMonth(payroll_key));
        if (row.size_label) item.payroll_size_label = row.size_label;
      }
      return item;
    });
    await productionRowsBulkUpdate(payload);
    await load();
  };

  const [progressCounts, setProgressCounts] = useState({});
  useEffect(() => {
    if (!progress) {
      setProgressCounts(rows.reduce((acc, r) => {
        acc[r.progress] = (acc[r.progress] || 0) + 1; return acc;
      }, {}));
    }
  }, [rows, progress]);

  const resetFilters = () => {
    setQ(""); setProduct(null); setColor(null); setSize(null);
    setStage(null); setStatus(null); setProgress(null); setFrom(null); setNoMonth(false);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          ثبت و مدیریت تولید{" "}
          <Text type="secondary" style={{ fontSize: 12 }}>
            (کنترل اول و کنترل نهایی داخل جزئیات هر کد تولید)
          </Text>
        </Title>
        <Space wrap>
          {monthScope.node}
          {monthScope.jy && monthClose && (
            <Tooltip title={monthClose.status === "closed"
              ? "تولیدِ این ماه تمام اعلام شده — محاسبهٔ حقوق می‌تواند نهایی شود"
              : `${fmtNum(monthClose.unfinished_rows)} ردیف هنوز نیمه‌کاره است`}>
              <Button size="small"
                type={monthClose.status === "closed" ? "default" : "primary"}
                danger={monthClose.status === "closed"}
                onClick={toggleMonthClose}>
                {monthClose.status === "closed"
                  ? "بازگشاییِ تولیدِ ماه" : "بستنِ تولیدِ ماه (آخرین سری)"}
              </Button>
            </Tooltip>
          )}
          {/* همان دکمه‌های صفحهٔ برش: آماده دوخت / در حال دوخت / کنترل اول / نهایی */}
          <Button size="small" type={!progress ? "primary" : "default"}
            onClick={() => setProgress(null)}>
            همه ({fmtNum(rows.length)})
          </Button>
          {PRODUCTION_STAGES.map((x) => (
            <Button key={x.value} size="small"
              type={progress === x.value ? "primary" : "default"}
              onClick={() => setProgress(progress === x.value ? null : x.value)}>
              {x.label} ({fmtNum(progressCounts[x.value] || 0)})
            </Button>
          ))}
          <Tooltip title="کارهایی که هنوز ماهِ محاسبهٔ حقوق نخورده‌اند">
            <Button size="small" type={noMonth ? "primary" : "default"} danger={noMonth}
              onClick={() => setNoMonth((x) => !x)}>
              بدونِ ماهِ محاسبه{!noMonth ? ` (${fmtNum(noMonthCount)})` : ""}
            </Button>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
          <Button icon={<DownloadOutlined />}
            onClick={() => exportCsv(columns, rows, `production-rows-${Date.now()}.csv`)}
            style={{ background: "#107C41", color: "#fff", borderColor: "#107C41" }}>
            خروجی اکسل
          </Button>
        </Space>
      </div>

      <DismissibleHelp id="production-manage" message="سه مقدار اصلی هر ردیف"
        description={"تعداد بیجک برش، تعداد کنترل اول، و سالم+زده‌دارِ کنترل نهایی کنار هم "
          + "مقایسه می‌شوند؛ اختلاف منفی یعنی کمتر از برش و مثبت یعنی بیشتر. جمع‌های زیر "
          + "لیست همیشه بر اساس همان فیلترِ جاری است."} />

      <Card size="small" style={{ marginBottom: 12, overflow: "hidden" }}>
        <Row gutter={[8, 8]} wrap>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Input.Search allowClear value={q} placeholder="جستجو: کد تولید / بیجک / محصول"
              onChange={(e) => setQ(e.target.value)} onSearch={load} style={{ width: "100%" }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select allowClear showSearch value={product} onChange={setProduct} options={opts.product}
              placeholder="محصول" style={{ width: "100%" }} optionFilterProp="label"
              getPopupContainer={(n) => n.parentElement} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Select allowClear value={color} onChange={setColor} options={opts.color}
              placeholder="رنگ" style={{ width: "100%" }}
              getPopupContainer={(n) => n.parentElement} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={2}>
            <Select allowClear value={size} onChange={setSize} options={opts.size}
              placeholder="سایز" style={{ width: "100%" }}
              getPopupContainer={(n) => n.parentElement} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Select allowClear value={progress} onChange={setProgress} options={PRODUCTION_STAGES}
              placeholder="وضعیت دوخت" style={{ width: "100%" }}
              getPopupContainer={(n) => n.parentElement} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Select allowClear value={stage} onChange={setStage} options={STAGE_OPTS}
              placeholder="مرحله" style={{ width: "100%" }}
              getPopupContainer={(n) => n.parentElement} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Select allowClear value={status} onChange={setStatus}
              options={statusOptions.map((s) => ({ value: s, label: STATUS_LABELS[s] || s }))}
              placeholder="وضعیت" style={{ width: "100%" }}
              getPopupContainer={(n) => n.parentElement} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            {/* یک تاریخ کافی است — «از این تاریخ به بعد» (تاریخِ دوم حذف شد تا فیلتر یک‌خطی بماند) */}
            <JalaliDateInput value={from} onChange={setFrom} style={{ width: "100%" }} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={2}>
            <Button size="small" type="link" onClick={resetFilters} style={{ paddingInline: 4 }}>
              پاک‌کردن
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={4}><Card size="small"><Statistic title="ردیف محصول" value={totals.rows || 0} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="جمع بیجک برش" value={totals.cut_qty || 0} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="جمع کنترل اول" value={totals.qc1_qty || 0} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="جمع سالم" value={totals.good_qty || 0} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="جمع زده‌دار" value={totals.defect_qty || 0} /></Card></Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="جمع کنترل نهایی" value={totals.final_qty || 0}
              suffix={<span style={{ fontSize: 12 }}>{diffTag(totals.final_diff, totals.final_diff_pct)}</span>} />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <EditableTable
          layoutId="production-rows"
          /* جدول ۲۱ ستون و ~۲۶۴۵px بود؛ روی هیچ نمایشگری کامل دیده نمی‌شد.
             سه ستونِ اولِ این فهرست عیناً تکرارِ ستونِ «محصول | رنگ | سایز»اند
             (بک‌اند همان سه را با variant به هم می‌چسباند)، بقیه هم اعدادِ
             تطبیقی‌اند که در کارِ روزانه لازم نیستند. هیچ ستونی حذف نشده —
             با دکمهٔ «ستون‌ها» برمی‌گردند و همان انتخاب ذخیره می‌شود. */
          gxDefaultHidden={PROD_DEFAULT_HIDDEN}
          /* بدونِ این، پیش‌فرضِ بالا فقط کاربرِ تازه را می‌گرفت: هرکس یک‌بار
             ستونی را باز/بسته کرده باشد چیدمانِ ذخیره‌شده دارد و پیش‌فرضِ
             جدید هرگز به او نمی‌رسید. */
          gxLayoutVersion="v2-density"
          dataSource={rows}
          columns={columns}
          rowKey="rid"
          loading={loading}
          gxSummary={gxSummary}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          sortDirections={["ascend", "descend"]}
          canEditRow={(r) => (r.status === "closed" ? "این تولید بسته شده است" : true)}
          onSaveMany={saveMany}
        />
      </Card>

      <BijackDrawer code={openCode} orderId={openOrderId} open={!!openCode}
        onClose={closeDrawer} onChanged={load} onOpenSibling={openDrawer} />
    </div>
  );
}
